import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Spin, Switch, Table, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import EditableDrawer from 'components/EditableDrawer';
import InputFilterItem from 'components/InputFilterItem';
import SingleSelect from 'components/SingleSelect';
import {
    TMS_TASK_DETAIL,
    TMS_TASK_FIELD_OPTIONS,
    TmsTaskDetailItem,
    TmsTaskDetailResponse,
    TmsTaskFieldOption,
    TmsTaskFieldOptionsResponse,
} from '../graphql/queries/taskFieldEditQueries';
import {
    DELETE_TMS_TASK,
    DeleteTmsTaskResponse,
    RESTORE_TMS_TASK,
    RestoreTmsTaskResponse,
    TmsTaskFieldsInput,
    UPDATE_TMS_TASK_FIELDS,
    UpdateTmsTaskFieldsResponse,
} from '../graphql/mutations/taskManagementMutations';
import {
    BUSINESS_TYPE_MAP,
    OpsDateTimeItem,
    OpsDateTimeKind,
    renderDeleted,
    renderTaskStatus,
    ROUTE_TYPE_MAP,
    TASK_STATUS_MAP,
    TASK_TYPE_MAP,
    usableZone,
    UTC_PATTERN,
    countChangedFields,
    RELOCK_DISCARD_HINT,
} from './taskManagementShared';

/**
 * 运维「编辑字段」抽屉：tms_task 全部可编辑字段 + tms_task_ext 司机字段，绕过业务状态机直接订正。
 * 只共用 components/ 下的通用组件（EditableDrawer / InputFilterItem / SingleSelect），与业务 EditTaskDrawer 零关联。
 * 表单值一律按字符串持有，空串=置空；保存前 diff 出改动并二次确认。
 */

dayjs.extend(utc);
dayjs.extend(timezone);

type Target = { id: number; taskNumber: string | null } | null;

type Props = {
    target: Target;
    onClose: () => void;
    onSuccess?: () => void;
};

type FieldTable = 'task' | 'ext';

type OptionsKey = keyof TmsTaskFieldOptionsResponse['opsToolTmsTaskFieldOptions'];

type FieldDef = {
    table: FieldTable;
    key: string;
    label: string;
    kind: 'text' | 'select';
    /** 静态枚举选项 */
    options?: { label: string; value: string }[];
    /** 外键：从 opsToolTmsTaskFieldOptions 取对应列表，带搜索 */
    optionsKey?: OptionsKey;
    placeholder?: string;
    required?: boolean;
    /** 只读展示：值由其他字段联动写入或默认不开放，「解除联动锁定」后可改 */
    readOnly?: boolean;
    /** 硬锁定：任何情况下不可编辑，解除联动锁定也不放开 */
    alwaysReadOnly?: boolean;
    /** 本地日期/时间：对应时区可用时由 UTC 时间点自动换算并锁定；时区缺失则可手填 */
    localOf?: 'origin' | 'destination';
};

const enumOptions = (map: Record<number, { label: string }>) =>
    Object.entries(map).map(([value, item]) => ({ value, label: item.label }));

const BOOL_OPTIONS = [
    { value: '1', label: '是' },
    { value: '0', label: '否' },
];

/** 跟车人数：1~3（用户 2026-09-28 指定），后端白名单同步为 1~3 */
const ESCORT_COUNT_OPTIONS = ['1', '2', '3'].map((v) => ({ value: v, label: v }));

const DT = 'yyyy-MM-dd HH:mm:ss';
const D = 'yyyy-MM-dd';
const T = 'HH:mm:ss';

/** 分区与字段定义，键名与后端白名单一致 */
const SECTIONS: { title: string; hint?: string; fields: FieldDef[] }[] = [
    {
        title: '基础信息',
        fields: [
            { table: 'task', key: 'taskNumber', label: '任务号', kind: 'text', required: true, readOnly: true },
            { table: 'task', key: 'taskStatus', label: '任务状态', kind: 'select', options: enumOptions(TASK_STATUS_MAP), required: true },
            { table: 'task', key: 'taskDate', label: '任务日期', kind: 'text', placeholder: D, required: true },
            { table: 'task', key: 'taskType', label: '任务类型', kind: 'select', options: enumOptions(TASK_TYPE_MAP), required: true },
            { table: 'task', key: 'businessType', label: '业务类型', kind: 'select', options: enumOptions(BUSINESS_TYPE_MAP), required: true, alwaysReadOnly: true },
            { table: 'task', key: 'bolNumber', label: 'BOL Number', kind: 'text', readOnly: true },
            { table: 'task', key: 'remark', label: '备注', kind: 'text' },
        ],
    },
    {
        title: '路线信息',
        hint: '路线ID、班次ID及随路线带出的路线名称、路线代码、路线类型、所属仓默认锁定，需「解除联动锁定」后修改；班次按路线过滤。',
        fields: [
            { table: 'task', key: 'routeId', label: '路线ID', kind: 'select', optionsKey: 'routes', required: true, readOnly: true },
            { table: 'task', key: 'routeName', label: '路线名称', kind: 'text', required: true, readOnly: true },
            { table: 'task', key: 'routeCode', label: '路线代码', kind: 'text', readOnly: true },
            { table: 'task', key: 'routeType', label: '路线类型', kind: 'select', options: enumOptions(ROUTE_TYPE_MAP), required: true, readOnly: true },
            { table: 'task', key: 'scheduleId', label: '班次ID', kind: 'select', optionsKey: 'schedules', readOnly: true },
            { table: 'task', key: 'warehouseId', label: '所属仓ID', kind: 'select', optionsKey: 'warehouses', required: true, readOnly: true },
        ],
    },
    {
        title: '时间',
        hint: '标注「(UTC)」的是 UTC 时间点；未标注的日期、时间均为仓库本地时间。发车类按起点仓时区、到达类按终点仓时区（IANA）换算。',
        fields: [
            { table: 'task', key: 'planDepartureDate', label: '计划发车日期', kind: 'text', placeholder: D, localOf: 'origin' },
            { table: 'task', key: 'planDepartureTime', label: '计划发车时间', kind: 'text', placeholder: T, localOf: 'origin' },
            { table: 'task', key: 'planDepartureAt', label: '计划发车(UTC)', kind: 'text', placeholder: DT },
            { table: 'task', key: 'actualDepartureDate', label: '实际发车日期', kind: 'text', placeholder: D, localOf: 'origin' },
            { table: 'task', key: 'actualDepartureTime', label: '实际发车时间', kind: 'text', placeholder: T, localOf: 'origin' },
            { table: 'task', key: 'actualDepartureAt', label: '实际发车(UTC)', kind: 'text', placeholder: DT },
            { table: 'task', key: 'planArrivalDate', label: '计划到达日期', kind: 'text', placeholder: D, localOf: 'destination' },
            { table: 'task', key: 'planArrivalTime', label: '计划到达时间', kind: 'text', placeholder: T, localOf: 'destination' },
            { table: 'task', key: 'planArrivalAt', label: '计划到达(UTC)', kind: 'text', placeholder: DT },
            { table: 'task', key: 'actualArrivalDate', label: '实际到达日期', kind: 'text', placeholder: D, localOf: 'destination' },
            { table: 'task', key: 'actualArrivalTime', label: '实际到达时间', kind: 'text', placeholder: T, localOf: 'destination' },
            { table: 'task', key: 'actualArrivalAt', label: '实际到达(UTC)', kind: 'text', placeholder: DT },
            { table: 'task', key: 'publishedAt', label: '发布时间(UTC)', kind: 'text', placeholder: DT },
            { table: 'task', key: 'completedAt', label: '完成时间(UTC)', kind: 'text', placeholder: DT },
            { table: 'task', key: 'completedDate', label: '完成日期', kind: 'text', placeholder: D },
        ],
    },
    {
        title: '承运 / 司机 / 车辆',
        hint: '带「(废弃)」的三个司机字段正在迁移到 tms_task_ext，但司机端仍读这里，迁移期两表双写。此处不可单独编辑，会随下方「扩展表」的司机 1 自动同步并一起保存。',
        fields: [
            { table: 'task', key: 'carrierId', label: '承运商ID', kind: 'select', optionsKey: 'carriers', required: true },
            { table: 'task', key: 'driverId', label: '司机ID(废弃)', kind: 'select', optionsKey: 'drivers', readOnly: true },
            { table: 'task', key: 'driverName', label: '司机名(废弃)', kind: 'text', readOnly: true },
            { table: 'task', key: 'phoneNumber', label: '电话(废弃)', kind: 'text', readOnly: true },
            { table: 'task', key: 'vehicleId', label: '车辆ID', kind: 'select', optionsKey: 'vehicles' },
            { table: 'task', key: 'vehicleTypeId', label: '车型ID', kind: 'select', optionsKey: 'vehicleTypes' },
            { table: 'task', key: 'licensePlate', label: '车牌', kind: 'text' },
            { table: 'task', key: 'trailerNumber', label: '柜号', kind: 'text' },
            { table: 'task', key: 'escortCount', label: '跟车人数', kind: 'select', options: ESCORT_COUNT_OPTIONS, required: true },
            { table: 'task', key: 'gpsTracking', label: 'GPS追踪', kind: 'text' },
        ],
    },
    {
        title: '扩展表',
        fields: [
            { table: 'ext', key: 'driver1Id', label: '司机1 ID', kind: 'select', optionsKey: 'drivers' },
            { table: 'ext', key: 'driver1Name', label: '司机1名称', kind: 'text' },
            { table: 'ext', key: 'driver1PhoneNumber', label: '司机1电话', kind: 'text' },
            { table: 'ext', key: 'driver2Id', label: '司机2 ID', kind: 'select', optionsKey: 'drivers' },
            { table: 'ext', key: 'driver2Name', label: '司机2名称', kind: 'text' },
            { table: 'ext', key: 'driver2PhoneNumber', label: '司机2电话', kind: 'text' },
            { table: 'ext', key: 'isBulkConsolidated', label: '散货拼车', kind: 'select', options: BOOL_OPTIONS },
            // 单车成本：GraphQL input 拍平只传 key，Java 侧收到后两表同写（用户 2026-10-21）；标 ext 使「无扩展行禁用」与确认框「写入两表」文案自动正确
            { table: 'ext', key: 'perVehicleCost', label: '单车成本(美分)', kind: 'text', placeholder: '整数' },
            { table: 'ext', key: 'cancelReason', label: '取消原因', kind: 'text' },
        ],
    },
];

const formKey = (f: FieldDef) => `${f.table}.${f.key}`;

/** 提交时的值类型：与后端 OpsTmsTaskFieldsInput 的字段类型一致（int/long → Number，boolean → 0/1 转布尔，其余字符串由标量校验格式） */
type ValueType = 'string' | 'int' | 'long' | 'boolean' | 'date' | 'time' | 'datetime';
const VALUE_TYPES: Record<string, ValueType> = {
    taskStatus: 'int', taskType: 'int', businessType: 'int', routeType: 'int', escortCount: 'int',
    perVehicleCost: 'long', routeId: 'long', scheduleId: 'long', warehouseId: 'long', carrierId: 'long', driverId: 'long',
    vehicleId: 'long', vehicleTypeId: 'long', driver1Id: 'long', driver2Id: 'long',
    isBulkConsolidated: 'boolean',
    taskDate: 'date', completedDate: 'date',
    planDepartureDate: 'date', actualDepartureDate: 'date', planArrivalDate: 'date', actualArrivalDate: 'date',
    planDepartureTime: 'time', actualDepartureTime: 'time', planArrivalTime: 'time', actualArrivalTime: 'time',
    planDepartureAt: 'datetime', actualDepartureAt: 'datetime', planArrivalAt: 'datetime', actualArrivalAt: 'datetime',
    publishedAt: 'datetime', completedAt: 'datetime',
};
const toTypedValue = (key: string, raw: string): string | number | boolean => {
    switch (VALUE_TYPES[key] ?? 'string') {
        case 'int':
        case 'long':
            return Number(raw);
        case 'boolean':
            return raw === '1' || raw === 'true';
        default:
            return raw;
    }
};

/** 交接类型（路线类型 = HANDOVER，含三方任务）：司机、车辆来自交接方/三方，无主数据，司机 ID / 车辆 ID 不可选，名称/电话/车牌手填（与业务 EditTaskDrawer 的 isHandoverRoute 口径一致） */
const HANDOVER_ROUTE_TYPE = '3';
/** 三方任务（任务类型 = ThirdParty）：承运商由三方链路决定（carrier_id=-1 等），不可在此改 */
const THIRD_PARTY_TASK_TYPE = '3';
const DRIVER_ID_KEYS = new Set(['ext.driver1Id', 'ext.driver2Id', 'task.driverId', 'task.vehicleId']);
/** 平台类业务（2-TikTok 3-Temu_GA 4-Temu_SCF）已完成（状态 4）：保存时二次弹窗警告，不拦截 */
const PLATFORM_BUSINESS_TYPES = new Set(['2', '3', '4']);
const COMPLETED_STATUS = 4;

/** 交接类型只有始发站，无到达相关字段（计划/实际到达 × 日期/时间/UTC）；也无单车成本（用户 2026-10-09）。抽屉中隐藏 */
const HANDOVER_HIDDEN_PREFIXES = ['task.planArrival', 'task.actualArrival', 'ext.perVehicleCost'];
const isHandoverHiddenKey = (k: string) => HANDOVER_HIDDEN_PREFIXES.some((p) => k.startsWith(p));
/** 任务表旧司机三字段：随扩展表司机 1 镜像，无扩展行时同样不可编辑（无源可镜像） */
const LEGACY_DRIVER_KEYS = new Set(['task.driverId', 'task.driverName', 'task.phoneNumber']);

/** UTC 时间点 → 本地日期/时间（发车类用起点仓时区，到达类用终点仓时区） */
const UTC_TO_LOCAL: Record<string, { date: string; time: string; zone: 'origin' | 'destination' }> = {
    'task.planDepartureAt': { date: 'task.planDepartureDate', time: 'task.planDepartureTime', zone: 'origin' },
    'task.actualDepartureAt': { date: 'task.actualDepartureDate', time: 'task.actualDepartureTime', zone: 'origin' },
    'task.planArrivalAt': { date: 'task.planArrivalDate', time: 'task.planArrivalTime', zone: 'destination' },
    'task.actualArrivalAt': { date: 'task.actualArrivalDate', time: 'task.actualArrivalTime', zone: 'destination' },
};

/** 司机电话：纯 10 位数字（与业务侧规则及后端白名单 PHONE 类型一致）；提交前校验，不合法拦截 */
const PHONE_PATTERN = /^\d{10}$/;
const PHONE_KEYS = ['ext.driver1PhoneNumber', 'ext.driver2PhoneNumber', 'task.phoneNumber'];

/** 承运商 / 交接方是否服务该仓：centerWarehouseIds 含仓 ID 或含 "0"（全国） */
const servesWarehouse = (o: TmsTaskFieldOption, warehouseId: string): boolean => {
    const ids = o.centerWarehouseIds ?? [];
    return ids.includes('0') || ids.includes(warehouseId);
};

/** 司机 ID 已选时，对应姓名/电话由账号带出、锁定不可编辑；清空 ID 后可手填 */
const LOCKED_BY_DRIVER_ID: Record<string, string> = {
    'ext.driver1Name': 'ext.driver1Id',
    'ext.driver1PhoneNumber': 'ext.driver1Id',
    'ext.driver2Name': 'ext.driver2Id',
    'ext.driver2PhoneNumber': 'ext.driver2Id',
    // 车辆 ID 已选时，车牌 / 车型由车辆带出、锁定；清空车辆 ID 后可手填
    'task.licensePlate': 'task.vehicleId',
    'task.vehicleTypeId': 'task.vehicleId',
};

/** 扩展表司机 1 → 任务表旧司机字段（双写，司机端仍读任务表），改前者时自动写后者 */
const MIRRORED_FROM_EXT: Record<string, string> = {
    'ext.driver1Id': 'task.driverId',
    'ext.driver1Name': 'task.driverName',
    'ext.driver1PhoneNumber': 'task.phoneNumber',
};

const FIELD_BY_KEY: Record<string, FieldDef> = {};
SECTIONS.forEach((s) => s.fields.forEach((f) => { FIELD_BY_KEY[formKey(f)] = f; }));
const labelOf = (k: string) => FIELD_BY_KEY[k]?.label ?? k;

/**
 * 级联一致性提示（仅提示不拦截）：同组字段只改了一部分时，提醒一并核对其余字段。
 * 组内任一字段有改动、且组内还有未改动字段 → 触发。
 */
const CASCADE_GROUPS: { name: string; keys: string[]; reason: string }[] = [
    { name: '司机1', keys: ['ext.driver1Id', 'ext.driver1Name', 'ext.driver1PhoneNumber'], reason: '司机名称/电话是司机ID的冗余快照（任务表旧司机三字段已自动跟随，无需单独处理）' },
    { name: '司机2', keys: ['ext.driver2Id', 'ext.driver2Name', 'ext.driver2PhoneNumber'], reason: '司机名称/电话是司机ID的冗余快照' },
    { name: '计划发车', keys: ['task.planDepartureDate', 'task.planDepartureTime', 'task.planDepartureAt'], reason: '本地日期+时间与 UTC 时间点应同步' },
    { name: '实际发车', keys: ['task.actualDepartureDate', 'task.actualDepartureTime', 'task.actualDepartureAt'], reason: '本地日期+时间与 UTC 时间点应同步' },
    { name: '计划到达', keys: ['task.planArrivalDate', 'task.planArrivalTime', 'task.planArrivalAt'], reason: '本地日期+时间与 UTC 时间点应同步' },
    { name: '实际到达', keys: ['task.actualArrivalDate', 'task.actualArrivalTime', 'task.actualArrivalAt'], reason: '本地日期+时间与 UTC 时间点应同步' },
    { name: '完成时间', keys: ['task.completedAt', 'task.completedDate'], reason: '完成日期是完成时间点的本地日期' },
];

/** 状态与时间字段的定向提示 */
const STATUS_HINTS: { status: string; label: string; expectKeys: string[] }[] = [
    { status: '2', label: '已发布', expectKeys: ['task.publishedAt'] },
    { status: '4', label: '已完成', expectKeys: ['task.completedAt', 'task.completedDate'] },
    { status: '6', label: '已取消', expectKeys: ['ext.cancelReason'] },
];

const buildCascadeWarnings = (changedKeys: Set<string>, after: Record<string, string>): string[] => {
    const warnings: string[] = [];
    CASCADE_GROUPS.forEach((g) => {
        const changed = g.keys.filter((k) => changedKeys.has(k));
        const untouched = g.keys.filter((k) => !changedKeys.has(k));
        if (changed.length > 0 && untouched.length > 0) {
            warnings.push(`【${g.name}】改了 ${changed.map(labelOf).join('、')}，但未改 ${untouched.map(labelOf).join('、')}。${g.reason}，建议一并核对，否则可能数据不一致。`);
        }
    });
    if (changedKeys.has('task.taskDate')) {
        warnings.push('【任务日期】已改，但任务号（含日期、不可编辑）保持不变，两者将不一致，请确认。');
    }
    if (changedKeys.has('task.taskStatus')) {
        STATUS_HINTS.forEach((h) => {
            if (after['task.taskStatus'] === h.status) {
                const untouched = h.expectKeys.filter((k) => !changedKeys.has(k));
                if (untouched.length > 0) {
                    warnings.push(`【任务状态】改为「${h.label}」，但未改 ${untouched.map(labelOf).join('、')}，建议一并核对。`);
                }
            }
        });
    }
    return warnings;
};

/** 后端值 → 表单字符串：null → ''，布尔 → '1'/'0'，其余 String() */
const toFormValue = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'boolean') return v ? '1' : '0';
    return String(v);
};

const buildFormValues = (detail: TmsTaskDetailItem): Record<string, string> => {
    const values: Record<string, string> = {};
    SECTIONS.forEach((s) => s.fields.forEach((f) => {
        values[formKey(f)] = toFormValue((detail as unknown as Record<string, unknown>)[f.key]);
    }));
    return values;
};

const TaskFieldEditDrawer: React.FC<Props> = ({ target, onClose, onSuccess }) => {
    const [values, setValues] = useState<Record<string, string>>({});
    /** 解除联动锁定：移除全部计算型 disabled，并停用自动带出/镜像/时区换算，按裸字段编辑（防联动算错时无法订正） */
    const [unlocked, setUnlocked] = useState(false);
    /** 解除业务类型锁定：业务类型默认硬锁定（改错会影响平台链路/结算口径），需单独开关放开，打开时二次确认 */
    const [businessTypeUnlocked, setBusinessTypeUnlocked] = useState(false);
    /** 解锁那一刻的字段快照：上锁 = 恢复到快照，解锁期间未保存的修改作废（用户 2026-10-21）；业务类型开关单独一份 */
    const unlockSnapshotRef = useRef<Record<string, string> | null>(null);
    const businessTypeSnapshotRef = useRef<string | null>(null);
    const [initial, setInitial] = useState<Record<string, string>>({});
    const { data, loading, refetch } = useQuery<TmsTaskDetailResponse>(TMS_TASK_DETAIL, {
        variables: { taskId: target?.id },
        skip: target === null,
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });
    const [runUpdate, { loading: saving }] = useMutation<UpdateTmsTaskFieldsResponse>(UPDATE_TMS_TASK_FIELDS, {
        onError: (e) => message.error(e.message),
    });
    // 外键下拉选项：抽屉打开时拉一次，缓存复用（主数据变化频率低）
    const { data: optionsData } = useQuery<TmsTaskFieldOptionsResponse>(TMS_TASK_FIELD_OPTIONS, {
        skip: target === null,
        onError: (e) => message.error(`下拉选项加载失败：${e.message}`),
    });
    const [runDelete, { loading: deleting }] = useMutation<DeleteTmsTaskResponse>(DELETE_TMS_TASK, {
        onError: (e) => message.error(e.message),
    });
    const [runRestore, { loading: restoring }] = useMutation<RestoreTmsTaskResponse>(RESTORE_TMS_TASK, {
        onError: (e) => message.error(e.message),
    });
    const detail = data?.opsToolTmsTaskDetail;

    /** 外键下拉：文案带 ID 便于按 ID 搜索；当前值不在列表时保留为一项，避免打开即被清空 */
    const optionsFor = (f: FieldDef, current: string): { label: string; value: string }[] => {
        if (f.options) return f.options;
        // 三方任务：carrier_id 固定 -1，承运信息来自三方链路，不给候选（只保留当前值）；
        // 交接类型任务：carrier_id 指向交接方（handover_carrier），数据源切换
        const optionsKey: OptionsKey | undefined = f.key === 'carrierId'
            ? (isThirdParty ? undefined : isHandover ? 'handoverCarriers' : f.optionsKey)
            : f.optionsKey;
        let list: TmsTaskFieldOption[] = (optionsKey && optionsData?.opsToolTmsTaskFieldOptions[optionsKey]) || [];
        // 班次隶属路线：只列已选路线下的班次；未选路线则不给候选（当前值仍保留为一项）
        if (f.key === 'scheduleId') {
            const routeId = values['task.routeId'] ?? '';
            list = routeId ? list.filter((o) => String(o.routeId ?? '') === routeId) : [];
        }
        // 承运商 / 交接方按中心仓服务范围过滤：centerWarehouseIds 含任务所属仓或含 "0"（全国）；未选所属仓则不给候选
        if (optionsKey === 'carriers' || optionsKey === 'handoverCarriers') {
            const warehouseId = values['task.warehouseId'] ?? '';
            list = warehouseId ? list.filter((o) => servesWarehouse(o, warehouseId)) : [];
        }
        // 司机 / 车辆隶属承运商：非交接任务按已选承运商过滤；未选承运商则不给候选
        if (!isHandover && (optionsKey === 'drivers' || optionsKey === 'vehicles')) {
            const carrierId = values['task.carrierId'] ?? '';
            list = carrierId ? list.filter((o) => String(o.carrierId ?? '') === carrierId) : [];
        }
        const mapped = list.map((o) => ({ value: String(o.value), label: `${o.value} · ${o.label}` }));
        if (current && !mapped.some((o) => o.value === current)) {
            mapped.unshift({ value: current, label: `${current} · (不在列表中)` });
        }
        return mapped;
    };

    useEffect(() => {
        if (!detail) return;
        const v = buildFormValues(detail);
        setValues(v);
        setInitial(v);
        setUnlocked(false);
        setBusinessTypeUnlocked(false);
        unlockSnapshotRef.current = null;
        businessTypeSnapshotRef.current = null;
    }, [detail]);

    const changes = useMemo(() => {
        const list: { field: FieldDef; before: string; after: string }[] = [];
        SECTIONS.forEach((s) => s.fields.forEach((f) => {
            const k = formKey(f);
            const before = initial[k] ?? '';
            const after = (values[k] ?? '').trim();
            if (before !== after) list.push({ field: f, before, after });
        }));
        return list;
    }, [values, initial]);

    const isHandover = (values['task.routeType'] ?? '') === HANDOVER_ROUTE_TYPE;
    const isThirdParty = (values['task.taskType'] ?? '') === THIRD_PARTY_TASK_TYPE;

    /**
     * 联动规则（纯函数）：把 key 的新值写入 next 后，按规则带出/镜像/换算其他字段。
     * 仅锁定态改字段时对单个 key 调用；解锁态不调用（各填各的），上锁即恢复解锁前快照、不做重算（用户 2026-10-21）。
     */
    const applyLinkage = (next: Record<string, string>, k: string, v: string) => {
        const mirror = MIRRORED_FROM_EXT[k];
        if (mirror) next[mirror] = v;
        // 选路线后带出并锁定路线名称/代码/类型（清空路线ID不清快照，避免误清）
        if (k === 'task.routeId' && v) {
            const route = (optionsData?.opsToolTmsTaskFieldOptions.routes ?? []).find((o) => String(o.value) === v);
            if (route) {
                next['task.routeName'] = route.routeName ?? '';
                next['task.routeCode'] = route.routeCode ?? '';
                next['task.routeType'] = route.routeType != null ? String(route.routeType) : '';
                // 所属仓 = 路线起点仓：带出并锁定，且走一遍所属仓联动（承运商 / 司机 / 车辆随之校验）
                if (route.startWarehouseId != null) {
                    const wh = String(route.startWarehouseId);
                    if ((next['task.warehouseId'] ?? '') !== wh) {
                        next['task.warehouseId'] = wh;
                        applyLinkage(next, 'task.warehouseId', wh);
                    }
                }
            }
            // 换路线后原班次若不属于新路线则清空，避免路线与班次错配
            const schedule = (optionsData?.opsToolTmsTaskFieldOptions.schedules ?? []).find((o) => String(o.value) === (next['task.scheduleId'] ?? ''));
            if (schedule && String(schedule.routeId ?? '') !== v) next['task.scheduleId'] = '';
        }
        // UTC 时间点 → 本地日期/时间：输入完整格式即换算；清空则同步清空；时区缺失不换算（分区下已有黄字提示），保持可手填
        const local = UTC_TO_LOCAL[k];
        if (local) {
            const zone = usableZone(local.zone === 'origin' ? detail?.originZone : detail?.destinationZone);
            const trimmed = v.trim();
            if (trimmed === '') {
                next[local.date] = '';
                next[local.time] = '';
            } else if (zone && UTC_PATTERN.test(trimmed)) {
                try {
                    const converted = dayjs.utc(trimmed, 'YYYY-MM-DD HH:mm:ss').tz(zone);
                    if (converted.isValid()) {
                        next[local.date] = converted.format('YYYY-MM-DD');
                        next[local.time] = converted.format('HH:mm:ss');
                    }
                } catch {
                    // 时区名不被浏览器识别：不换算，本地字段保持可手填
                }
            }
        }
        // 切换所属仓：原承运商 / 交接方不再覆盖该仓则清空，并级联清空其司机 / 车辆
        if (k === 'task.warehouseId' && (next['task.taskType'] ?? '') !== THIRD_PARTY_TASK_TYPE) {
            const handoverNow = (next['task.routeType'] ?? '') === HANDOVER_ROUTE_TYPE;
            const pool = optionsData?.opsToolTmsTaskFieldOptions[handoverNow ? 'handoverCarriers' : 'carriers'] ?? [];
            const carrierId = next['task.carrierId'] ?? '';
            const current = pool.find((o) => String(o.value) === carrierId);
            if (carrierId && (!v || !current || !servesWarehouse(current, v))) {
                next['task.carrierId'] = '';
                applyLinkage(next, 'task.carrierId', '');
            }
        }
        // 切换承运商（非交接）：原司机 / 车辆不再隶属新承运商，清空待重选；带出的姓名电话与任务表镜像一并清空
        if (k === 'task.carrierId' && (next['task.routeType'] ?? '') !== HANDOVER_ROUTE_TYPE) {
            const stillBelongs = (key: OptionsKey, id: string) =>
                !!id && (optionsData?.opsToolTmsTaskFieldOptions[key] ?? []).some((o) => String(o.value) === id && String(o.carrierId ?? '') === v);
            if (!stillBelongs('drivers', next['ext.driver1Id'] ?? '')) {
                ['ext.driver1Id', 'ext.driver1Name', 'ext.driver1PhoneNumber', 'task.driverId', 'task.driverName', 'task.phoneNumber'].forEach((key) => { next[key] = ''; });
            }
            if (!stillBelongs('drivers', next['ext.driver2Id'] ?? '')) {
                ['ext.driver2Id', 'ext.driver2Name', 'ext.driver2PhoneNumber'].forEach((key) => { next[key] = ''; });
            }
            // 车辆随承运商清空时，由车辆带出的车牌 / 车型一并清空；本就未选车辆（手填车牌）则不动
            if ((next['task.vehicleId'] ?? '') && !stillBelongs('vehicles', next['task.vehicleId'] ?? '')) {
                ['task.vehicleId', 'task.licensePlate', 'task.vehicleTypeId'].forEach((key) => { next[key] = ''; });
            }
        }
        // 选车辆 ID 时带出车牌 / 车型（交接 / 三方任务无车辆主数据，跳过）；清空 ID 不清车牌车型
        if (k === 'task.vehicleId' && v && (next['task.routeType'] ?? '') !== HANDOVER_ROUTE_TYPE) {
            const vehicle = (optionsData?.opsToolTmsTaskFieldOptions.vehicles ?? []).find((o) => String(o.value) === v);
            if (vehicle) {
                if (vehicle.licensePlate) next['task.licensePlate'] = vehicle.licensePlate;
                next['task.vehicleTypeId'] = vehicle.vehicleTypeId != null ? String(vehicle.vehicleTypeId) : '';
            }
        }
        // 选司机 1/2 ID 时按账号带出司机名与电话（交接任务无司机主数据，跳过）；清空 ID 不清名称电话
        const driverSlot = k === 'ext.driver1Id' ? '1' : k === 'ext.driver2Id' ? '2' : null;
        const handover = (next['task.routeType'] ?? '') === HANDOVER_ROUTE_TYPE;
        if (driverSlot && v && !handover) {
            const opt = (optionsData?.opsToolTmsTaskFieldOptions.drivers ?? []).find((o) => String(o.value) === v);
            const name = opt ? opt.label.replace(/\s*\(账号 \d+\)$/, '') : '';
            const nameKey = `ext.driver${driverSlot}Name`;
            const phoneKey = `ext.driver${driverSlot}PhoneNumber`;
            if (name && name !== '-') next[nameKey] = name;
            if (opt?.phone) next[phoneKey] = opt.phone;
            // 司机 1 镜像到任务表旧字段
            if (driverSlot === '1') {
                if (name && name !== '-') next['task.driverName'] = name;
                if (opt?.phone) next['task.phoneNumber'] = opt.phone;
            }
        }
    };

    const setField = (f: FieldDef, v: string) => setValues((prev) => {
        const k = formKey(f);
        const next = { ...prev, [k]: v };
        if (!unlocked) applyLinkage(next, k, v);
        return next;
    });

    /**
     * 解除业务类型锁定：打开时二次确认（业务类型决定平台链路 / 结算口径）；
     * 关闭 = 业务类型回到解锁前的值，解锁期间的修改作废，有改动先确认（用户 2026-10-21，与「解除联动锁定」同语义）
     */
    const handleBusinessTypeUnlockChange = (on: boolean) => {
        if (!on) {
            const snapshot = businessTypeSnapshotRef.current;
            const relock = () => {
                if (snapshot !== null) setValues((prev) => ({ ...prev, 'task.businessType': snapshot }));
                businessTypeSnapshotRef.current = null;
                setBusinessTypeUnlocked(false);
            };
            if (snapshot === null || (values['task.businessType'] ?? '') === snapshot) {
                relock();
                return;
            }
            Modal.confirm({
                title: '上锁将放弃业务类型的修改',
                icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                okText: '放弃修改并上锁',
                okButtonProps: { danger: true },
                content: '业务类型将恢复为解锁前的值，解锁期间的修改作废。要保留修改，请先点「保存」再上锁。确认上锁？',
                onOk: relock,
            });
            return;
        }
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认解除',
            okButtonProps: { danger: true },
            content: '业务类型决定任务走哪条平台链路（TikTok / Temu 回传、结算口径等），改错会导致数据流向错误。确认解除锁定并允许修改业务类型？',
            onOk: () => {
                businessTypeSnapshotRef.current = values['task.businessType'] ?? '';
                setBusinessTypeUnlocked(true);
            },
        });
    };

    /**
     * 解除联动锁定：解锁时拍快照；上锁 = 全部字段恢复到快照，解锁期间未保存的修改作废，有改动先确认
     * （用户 2026-10-21，替代原「重新上锁按源值重算全部派生字段」——那会把未碰过的字段用主数据覆盖）
     */
    const handleUnlockChange = (on: boolean) => {
        if (on) {
            unlockSnapshotRef.current = { ...values };
            setUnlocked(true);
            return;
        }
        const snapshot = unlockSnapshotRef.current;
        const relock = () => {
            if (snapshot) setValues(snapshot);
            unlockSnapshotRef.current = null;
            setUnlocked(false);
        };
        const changedCount = snapshot ? countChangedFields(snapshot, values) : 0;
        if (changedCount === 0) {
            relock();
            return;
        }
        Modal.confirm({
            title: '上锁将放弃解锁期间的修改',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: `放弃 ${changedCount} 处修改并上锁`,
            okButtonProps: { danger: true },
            content: `解锁后改过的 ${changedCount} 个字段将恢复为解锁前的值。要保留这些修改，请先点「保存」再上锁。确认上锁？`,
            onOk: relock,
        });
    };

    const doSave = async () => {
        if (!target || !detail) return;
        // 强类型补丁三态：未改的字段不传（不改）；清空的传 null（置空）；其余按类型转换后传值
        const fields: TmsTaskFieldsInput = {};
        changes.forEach((c) => {
            fields[c.field.key] = c.after === '' ? null : toTypedValue(c.field.key, c.after);
        });
        const result = await runUpdate({
            variables: { taskId: target.id, expectedUpdatedAt: detail.updatedAt, fields },
        });
        const payload = result.data?.opsToolUpdateTmsTaskFields;
        if (!payload) return;
        if (!payload.success) {
            message.error(payload.message);
            return;
        }
        message.success(`任务 ${target.id} 已更新 ${changes.length} 个字段`);
        await refetch();
        onSuccess?.();
    };

    const handleSubmit = () => {
        if (!detail) return;
        if (changes.length === 0) {
            message.info('没有任何改动');
            return;
        }
        const missingRequired = changes.filter((c) => c.field.required && c.after === '');
        if (missingRequired.length > 0) {
            message.warning(`以下字段不允许为空：${missingRequired.map((c) => c.field.label).join('、')}`);
            return;
        }
        if (!detail.extExists && changes.some((c) => c.field.table === 'ext')) {
            message.warning('该任务没有扩展行（tms_task_ext），无法订正司机字段');
            return;
        }
        const badNumbers = changes.filter((c) => ['int', 'long'].includes(VALUE_TYPES[c.field.key] ?? '') && c.after !== '' && !/^-?\d+$/.test(c.after));
        if (badNumbers.length > 0) {
            message.warning(`须为整数：${badNumbers.map((c) => `${c.field.label}「${c.after}」`).join('、')}`);
            return;
        }
        const badPhones = changes.filter((c) => PHONE_KEYS.includes(formKey(c.field)) && c.after !== '' && !PHONE_PATTERN.test(c.after));
        if (badPhones.length > 0) {
            message.warning(`电话须为 10 位数字：${badPhones.map((c) => `${c.field.label}「${c.after}」`).join('、')}`);
            return;
        }
        const changedKeys = new Set(changes.map((c) => formKey(c.field)));
        const afterMap: Record<string, string> = {};
        changes.forEach((c) => { afterMap[formKey(c.field)] = c.after; });
        const warnings = buildCascadeWarnings(changedKeys, afterMap);
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: warnings.length > 0 ? '我已核对，确认保存' : '确认保存',
            okButtonProps: { danger: true },
            width: 760,
            content: (
                <div className="space-y-2">
                    <div>
                        即将直接改写任务 {detail.id}（{detail.taskNumber ?? '-'}）以下 {changes.length} 个字段。
                        不校验业务状态、不发事件、不维护关联一致性（如改路线不重建途经点、改司机不同步扩展表），确认继续？
                    </div>
                    {unlocked && (
                        <Alert type="error" showIcon message="本次在「解除联动锁定」状态下保存：系统未做任何自动带出与换算，所有值以你填写的为准。" />
                    )}
                    {warnings.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message="级联字段提示（仅提示，不拦截）"
                            description={<ul className="pl-4 list-disc space-y-1">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
                        />
                    )}
                    <Table
                        size="small"
                        pagination={false}
                        rowKey={(r) => formKey(r.field)}
                        dataSource={changes}
                        columns={[
                            { title: '字段', width: 160, render: (_, r) => `${r.field.label}（${r.field.table}.${r.field.key}）` },
                            { title: '改前', dataIndex: 'before', render: (v: string) => v || <span className="text-gray-400">空</span> },
                            { title: '改后', dataIndex: 'after', render: (v: string) => v || <span className="text-gray-400">空</span> },
                        ]}
                    />
                </div>
            ),
            onOk: () => {
                // 高风险改动逐个再确认（顺序：业务类型变更 → 平台类任务已完成），任一取消即放弃保存；仍不拦截
                const extraConfirms: { title: string; content: string }[] = [];
                const businessTypeChange = changes.find((c) => formKey(c.field) === 'task.businessType');
                if (businessTypeChange) {
                    const label = (v: string) => BUSINESS_TYPE_MAP[Number(v)]?.label ?? v ?? '空';
                    extraConfirms.push({
                        title: '再次确认：修改业务类型',
                        content: `业务类型将从「${label(businessTypeChange.before)}」改为「${label(businessTypeChange.after)}」。业务类型决定任务走哪条平台链路（回传、结算口径），改错会导致数据流向错误。确定继续保存？`,
                    });
                }
                const platformCompleted = PLATFORM_BUSINESS_TYPES.has(String(detail.businessType ?? '')) && detail.taskStatus === COMPLETED_STATUS;
                if (platformCompleted) {
                    extraConfirms.push({
                        title: '再次确认：平台类任务已完成',
                        content: `任务 ${detail.id}（${detail.taskNumber ?? '-'}）属于 TikTok / Temu_GA / Temu_SCF 且已完成，相关数据可能已回传平台或进入结算，改动不会同步到平台侧。确定继续保存？`,
                    });
                }
                // 任何保存都再确认一次（用户 2026-10-09）：直接改写库表、不经业务校验、无自动撤销
                const tables = changes.some((c) => c.field.table === 'ext') ? 'tms_task / tms_task_ext' : 'tms_task';
                extraConfirms.push({
                    title: '再次确认：直接改写任务数据',
                    content: `这 ${changes.length} 个字段将直接写入 ${tables}，不经业务校验，没有自动撤销手段，只能再次手工订正。确定保存？`,
                });
                return new Promise<void>((resolve) => {
                    const step = (i: number) => {
                        if (i >= extraConfirms.length) {
                            doSave().then(() => resolve(), () => resolve());
                            return;
                        }
                        Modal.confirm({
                            title: extraConfirms[i].title,
                            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                            okText: '我已知晓，继续',
                            okButtonProps: { danger: true },
                            content: extraConfirms[i].content,
                            onOk: () => step(i + 1),
                            onCancel: () => resolve(),
                        });
                    };
                    step(0);
                });
            },
        });
    };

    const confirmDelete = () => {
        if (!detail) return;
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认删除',
            okButtonProps: { danger: true },
            content: `即将把任务 ${detail.id}（${detail.taskNumber ?? '-'}）连同其途经点、扩展信息一起逻辑删除，不校验任务状态、不发事件；删除后可在本抽屉恢复。确认继续？`,
            onOk: async () => {
                const result = await runDelete({ variables: { taskId: detail.id } });
                const payload = result.data?.opsToolDeleteTmsTask;
                if (!payload) return;
                if (!payload.success) {
                    message.error(payload.message);
                    return;
                }
                message.success(`任务 ${detail.id} 已删除`);
                await refetch();
                onSuccess?.();
            },
        });
    };

    const confirmRestore = () => {
        if (!detail) return;
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认恢复',
            okButtonProps: { danger: true },
            content: `即将把已删除任务 ${detail.id}（${detail.taskNumber ?? '-'}）连同其途经点、扩展信息一起恢复为未删除状态，任务状态保持删除前不变。确认继续？`,
            onOk: async () => {
                const result = await runRestore({ variables: { taskId: detail.id } });
                const payload = result.data?.opsToolRestoreTmsTask;
                if (!payload) return;
                if (!payload.success) {
                    message.error(payload.message);
                    return;
                }
                message.success(`任务 ${detail.id} 已恢复`);
                await refetch();
                onSuccess?.();
            },
        });
    };

    /** 底部：左侧 删除/恢复 二选一（红色），右侧 保存/取消 */
    const footer = (
        <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
                {detail && (detail.isDeleted ? (
                    <Button danger loading={restoring} onClick={confirmRestore}>恢复任务</Button>
                ) : (
                    <Button danger loading={deleting} onClick={confirmDelete}>删除任务</Button>
                ))}
                <span className="flex items-center space-x-2 text-sm">
                    <Switch size="small" checked={unlocked} onChange={handleUnlockChange} disabled={!detail} />
                    <span className={unlocked ? 'text-orange-500' : 'text-gray-500'}>解除联动锁定</span>
                </span>
                <span className="flex items-center space-x-2 text-sm">
                    <Switch size="small" checked={businessTypeUnlocked} onChange={handleBusinessTypeUnlockChange} disabled={!detail} />
                    <span className={businessTypeUnlocked ? 'text-orange-500' : 'text-gray-500'}>解除业务类型锁定</span>
                </span>
            </div>
            <div className="space-x-4">
                <Button type="primary" onClick={handleSubmit} loading={saving} disabled={!detail || changes.length === 0 || saving}>
                    保存
                </Button>
                <Button onClick={onClose}>取消</Button>
            </div>
        </div>
    );

    const title = detail ? (
        <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">
            {detail.taskNumber ?? `任务 ${detail.id}`}
            <span className="ml-3 align-middle">{renderTaskStatus(detail.taskStatus)}{renderDeleted(detail.isDeleted)}</span>
        </span>
    ) : (target?.taskNumber ?? '编辑字段');

    return (
        <EditableDrawer
            title={title}
            visible={target !== null}
            onClose={onClose}
            footer={footer}
            width={1100}
            destroyOnClose
            zIndex={1000}
        >
            {loading || !detail ? (
                <div className="py-10 text-center"><Spin /></div>
            ) : (
                <div className="bg-white">
                    <div className="px-1 mb-3 text-gray-500 text-sm">
                        ID {detail.id}　创建 {detail.createdAt ?? '-'}　更新 {detail.updatedAt ?? '-'}（保存时作乐观锁）
                    </div>
                    {SECTIONS.map((section) => (
                        <div key={section.title} className="mb-4">
                            <div className="relative mt-2 mb-3 px-1">
                                <span className="text-black font-bold text-base">
                                    {/* 交接类型任务：承运商字段存的是交接方，分区标题与「交接方ID」标签保持一致 */}
                                    {section.title === '承运 / 司机 / 车辆' && isHandover && !isThirdParty ? '交接方 / 司机 / 车辆' : section.title}
                                </span>
                                {section.hint && <div className="text-gray-400 text-xs mt-1">{section.hint}</div>}
                            </div>
                            {section.title === '扩展表' && (
                                <div className="px-1 -mt-2 mb-3 text-gray-500 text-xs">单车成本保存时同时写入 tms_task 与 tms_task_ext（两表双写，显示值以扩展表为准）。</div>
                            )}
                            {section.title === '扩展表' && isHandover && (
                                <div className="px-1 -mt-2 mb-3 text-orange-500 text-xs">交接 / 三方任务：司机由交接方或三方提供，无司机主数据，司机 ID 不可选，请手填名称与电话。</div>
                            )}
                            {section.title === '时间' && isHandover && (
                                <div className="px-1 -mt-2 mb-3 text-gray-500 text-xs">交接类型任务只有始发站，无到达相关字段。</div>
                            )}
                            {section.title === '时间' && (usableZone(detail.originZone) || (!isHandover && usableZone(detail.destinationZone))) && (
                                <div className="px-1 -mt-2 mb-3 text-gray-500 text-xs">
                                    {usableZone(detail.originZone) && <span>起点仓时区 {usableZone(detail.originZone)}</span>}
                                    {usableZone(detail.originZone) && !isHandover && usableZone(detail.destinationZone) && <span>　</span>}
                                    {!isHandover && usableZone(detail.destinationZone) && <span>终点仓时区 {usableZone(detail.destinationZone)}</span>}
                                </div>
                            )}
                            {section.title === '时间' && (usableZone(detail.originZone) || (!isHandover && usableZone(detail.destinationZone))) && !unlocked && (
                                <div className="px-1 -mt-2 mb-3 text-gray-500 text-xs">本地日期 / 时间由 UTC 按上述时区自动算出并锁定，只需填 UTC；要手动改本地时间，请打开底部「解除联动锁定」。</div>
                            )}
                            {section.title === '时间' && (usableZone(detail.originZone) || (!isHandover && usableZone(detail.destinationZone))) && unlocked && (
                                <div className="px-1 -mt-2 mb-3 text-orange-500 text-xs">已解除联动锁定：修改 UTC 不再自动算出本地日期 / 时间，三个值各填各的，系统不校验它们是否对得上，保存前请自行核对。{RELOCK_DISCARD_HINT}</div>
                            )}
                            {section.title === '时间' && !usableZone(detail.originZone) && (
                                <div className="px-1 -mt-2 mb-3 text-orange-500 text-xs">起点仓时区缺失：发车类本地日期/时间不自动换算，请手填。</div>
                            )}
                            {section.title === '时间' && !isHandover && !usableZone(detail.destinationZone) && (
                                <div className="px-1 -mt-2 mb-3 text-orange-500 text-xs">终点仓时区缺失：到达类本地日期/时间不自动换算，请手填。</div>
                            )}
                            {section.title === '路线信息' && isThirdParty && (
                                <div className="px-1 -mt-2 mb-3 text-orange-500 text-xs">三方任务：无路线，路线ID不可编辑。</div>
                            )}
                            {section.title === '承运 / 司机 / 车辆' && isHandover && !isThirdParty && (
                                <div className="px-1 -mt-2 mb-3 text-gray-500 text-xs">交接类型任务：承运商字段存的是交接方（handover_carrier），下拉已切换为交接方列表；司机、车辆由交接方提供，司机 ID / 车辆 ID 不可选，请手填车牌、柜号。</div>
                            )}
                            {section.title === '承运 / 司机 / 车辆' && isThirdParty && (
                                <div className="px-1 -mt-2 mb-3 text-orange-500 text-xs">三方任务：carrier_id 固定为 -1，承运信息来自三方链路，此处不可编辑；司机 ID / 车辆 ID 不可选，司机名称、电话、车牌、柜号手填。</div>
                            )}
                            <div className="grid grid-cols-3 gap-4 px-4">
                                {section.fields.filter((f) => !(isHandover && isHandoverHiddenKey(formKey(f)))).map((f) => {
                                    const k = formKey(f);
                                    const lockedBy = isHandover ? undefined : LOCKED_BY_DRIVER_ID[k];
                                    const label = f.key === 'carrierId' && isHandover && !isThirdParty ? '交接方ID' : f.label;
                                    const zoneForLocal = usableZone(f.localOf === 'origin' ? detail.originZone : f.localOf === 'destination' ? detail.destinationZone : null);
                                    const hardDisabled = (!!f.alwaysReadOnly && !(k === 'task.businessType' && businessTypeUnlocked))
                                        || (f.table === 'ext' && !detail.extExists)
                                        || (!detail.extExists && LEGACY_DRIVER_KEYS.has(k));
                                    const computedDisabled = !!f.readOnly
                                        || (lockedBy !== undefined && !!values[lockedBy])
                                        || (f.localOf !== undefined && !!zoneForLocal)
                                        || (isHandover && DRIVER_ID_KEYS.has(k))
                                        || (isThirdParty && k === 'task.carrierId');
                                    const disabled = hardDisabled || (!unlocked && computedDisabled);
                                    const valueType = VALUE_TYPES[f.key];
                                    const dateTimeKind: OpsDateTimeKind | null = valueType === 'date' || valueType === 'time' || valueType === 'datetime' ? valueType : null;
                                    return (
                                        <div key={k}>
                                            {f.kind === 'select' ? (
                                                <SingleSelect
                                                    label={label}
                                                    options={optionsFor(f, values[k] ?? '')}
                                                    value={values[k] || undefined}
                                                    onChange={(v) => setField(f, v ?? '')}
                                                    spanClassName="w-[9em]"
                                                    placeholder={disabled ? '' : undefined}
                                                    disabled={disabled}
                                                    allowClear={!f.required}
                                                    forceHorizontal
                                                />
                                            ) : dateTimeKind ? (
                                                <OpsDateTimeItem
                                                    label={label}
                                                    kind={dateTimeKind}
                                                    value={values[k] ?? ''}
                                                    onChange={(v) => setField(f, v)}
                                                    disabled={disabled}
                                                />
                                            ) : (
                                                <InputFilterItem
                                                    label={label}
                                                    value={values[k] ?? ''}
                                                    onChange={(e) => setField(f, e.target.value)}
                                                    spanClassName="w-[9em]"
                                                    placeholder={disabled ? '' : (f.placeholder ?? '')}
                                                    disabled={disabled}
                                                    forceHorizontal
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </EditableDrawer>
    );
};

export default TaskFieldEditDrawer;
